<template>
    <SettingsSection :section="reconstructedSection" :index="index" :level="level" :isLoading="isLoading"
        :initiated="initiated" @debounceSave="debouncedAutoSave()" @instantSave="saveSettings">

        <template v-slot:prepend-settingList>
            <v-row no-gutters class="pb-4">
                <v-col cols="7">
                    <h5 v-text="'Logo'" style="opacity: 0.9;" />
                    <p v-text="'Company logo. Shown in the applications and visible to end customers in some reports.'"
                        class="ma-0" style="opacity: 0.8; white-space: pre-line;" />
                </v-col>
                <v-col cols="5">
                    <v-skeleton-loader v-if="!initiated" type="list-item">
                    </v-skeleton-loader>
                    <div v-else class="d-flex align-center justify-end">
                        <v-card flat outlined :key="resetLogoTrigger">
                            <QImage2 :size="65" :horizontalMultiplier="3.8" :fallbackIcon="'mdi-image'"
                                :openPickerOnClick="true" :editable="true" src="/api2/report/logo"
                                @File="file => saveLogo(file)">
                            </QImage2>
                        </v-card>
                        <v-btn icon @click="removeLogo()">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </v-col>
            </v-row>
        </template>

        <template v-slot:append-settingList>
            <v-row no-gutters class="pb-4">
                <v-col cols="7">
                    <h5 v-text="'Address'" style="opacity: 0.9;" />
                    <p v-text="'Address is shown in interfaces, reports and used to center the planning map.'"
                        class="ma-0" style="opacity: 0.8; white-space: pre-line;" />
                </v-col>
                <v-col cols="5" :key="'add' + resetAddressTrigger">
                    <v-skeleton-loader v-if="!initiated" type="list-item">
                    </v-skeleton-loader>
                    <div v-else class="d-flex flex-column">
                        <QTextField v-model="address"
                            :label="$t('servicesystem.common_objects.company_settings.street_address')" externaledit>
                        </QTextField>
                        <QTextField v-model="zip" class="my-3"
                            :label="$t('servicesystem.common_objects.company_settings.postcode_and_city')" externaledit>
                        </QTextField>
                    </div>
                </v-col>
                <v-col cols="12" style="height: 250px" v-if="initiated">
                    <Maps :center="mainLocation" style="width: 100%; height: 100%;" :zoom="10">
                        <MapMarker :key="'mark' + resetMarkerTrigger" @mouseup="ev => resolvePosition(ev)"
                            @position_changed="ev => (markerLocation = { lat: ev.lat(), lng: ev.lng() })"
                            :position="markerLocation" draggable>
                        </MapMarker>
                    </Maps>
                </v-col>
            </v-row>
        </template>

    </SettingsSection>
</template>


<script>
import Vue from 'vue';
import clientAPI from "@/api/ClientAPIMixin.js";
import Section from "./Section.js"
import QImage2 from "@/components/QImage2.vue";
import SettingsMixin from './SettingsMixin.js';
const SettingsSection = () => import("./SettingsSection.vue");

const addressKey = 'ServiceProvider.AddressStreet';
const zipKey = 'ServiceProvider.AddressZipAndCity';
const geoKey = 'MainLocationCoordinates';

export default {
    name: "CompanySettingsSection",
    props: {
        section: Section,
        index: Number,
        level: Number,
        isLoading: Boolean,
        initiated: Boolean,
    },
    components: {
        SettingsSection,
        QImage2,
    },
    mixins: [clientAPI, SettingsMixin],

    data() {
        return {
            logo: null,
            resetLogoTrigger: 1,
            resetAddressTrigger: 1,
            resetMarkerTrigger: 1,
        };
    },

    methods: {
        saveLogo(file) {
            clientAPI.methods.setSiteLogo(file)
                .then(() => {
                    //this.resetLogoTrigger++;
                });
        },

        async removeLogo() {
            await clientAPI.methods.setSiteLogo(null);
            //this.resetLogoTrigger++;
        },

        handleModifiedSetting(setting) {
            //console.log('on input')
            if (this.fetchLoading || !this.settingsValueHasChanged(setting)) {
                return;
            }

            Vue.set(setting, '_dirty', true);

            this.$emit('debounceSave');
            console.log('debounceSave', setting.settingKey)
        },

        saveSettings(settingsArr) {
            this.$emit('instantSave', settingsArr)
        },

        debouncedAutoSave() {
            this.$emit('debounceSave')
        },

        resolvePosition(args) {
            //console.log(args);
            const lat = args.latLng.lat();
            const lng = args.latLng.lng();
            const googleEvtCoordinate = { Longitude: lng, Latitude: lat };
            clientAPI.methods.getAddressFromPosition(googleEvtCoordinate).then(respponseAddress => {
                console.log(respponseAddress);
                if (respponseAddress.Street) {
                    var sts = this.settingMap[addressKey];
                    var zps = this.settingMap[zipKey];
                    var gos = this.settingMap[geoKey];

                    sts.value = `${respponseAddress.Street} ${respponseAddress.StreetNumber}`;
                    zps.value = `${respponseAddress.Zip} ${respponseAddress.City}`;
                    gos.value = `${lng}, ${lat}`;

                    this.saveSettings([sts, zps, gos]);
                    this.resetAddressTrigger++;
                }
                else {
                    this.resetMarkerTrigger++;
                }

            });
        },

        async trySetGeo() {
            var map = this.settingMap;
            var st = map[addressKey].value;
            var zp = map[zipKey].value;
            if (st && zp) {
                var response = await clientAPI.methods.getCoordinateFromAddress({ Street: st, City: zp });
                if (response) {
                    var setting = map[geoKey];
                    setting.value = `${response.Longitude}, ${response.Latitude}`;
                    this.handleModifiedSetting(setting);
                    this.resetMarkerTrigger++;
                }
            }
        },
    },

    computed: {
        address: {
            get() {
                return this.settingMap[addressKey].value;
            },
            set(val) {
                var setting = this.settingMap[addressKey];
                setting.value = val;
                this.trySetGeo();
                this.handleModifiedSetting(setting);
            },
        },
        zip: {
            get() {
                return this.settingMap[zipKey].value;
            },
            set(val) {
                var setting = this.settingMap[zipKey];
                setting.value = val;
                this.trySetGeo();
                this.handleModifiedSetting(setting);
            },
        },
        markerLocation: {
            get: function () { return this.mainLocation },
            set: function (/*args*/) {
                //console.log(args);
            }
        },
        mainLocation() {
            const mainLocationSetting = this.settingMap[geoKey].value;
            if (mainLocationSetting) {
                const s = mainLocationSetting.split(",");
                return { lng: parseFloat(s[0].trim()), lat: parseFloat(s[1].trim()) };
            }
            else {
                return null
            }
        },

        reconstructedSection() {
            var hiddenSettingKeys = [addressKey, zipKey, geoKey];
            var obj = new Section({
                ...this.section,
                settingList: this.section.settingList.filter(x => !hiddenSettingKeys.includes(x.settingKey))
            });
            //console.log('obj', obj);
            return obj;
        },

        settingMap() {
            var obj = {};
            this.section.settingList.forEach(s => {
                obj[s.settingKey] = s;
            });
            return obj;
        },
    },

    watch: {
        address: function () {
            this.resetMarkerTrigger;
        },
    },
};
</script>


<style lang="scss" scoped></style>